import { useState } from "react";
import { motion } from "framer-motion";
import { api, apiError } from "@/lib/api";
import { Nav } from "@/components/marketing/Nav";
import { CtaFooter } from "@/components/marketing/CtaFooter";
import { Reveal } from "@/components/Reveal";

const inputCls =
  "w-full bg-white border border-navy/15 px-4 py-3 text-sm text-navy focus:outline-none focus:border-gold transition-colors";
const labelCls = "font-mono text-[10px] tracking-[0.25em] uppercase text-navy/50 block mb-2";

const SERVICES = [
  "Tier 1 — Heart Health Check",
  "Tier 2 — Extended Cardiac Assessment",
  "Tier 3 — Specialist Cardiac Assessment",
  "Add-ons (ABPM / Ambulatory ECG / AF events)",
  "Full Programme Proposal",
];

const initial = { name: "", company: "", role: "", email: "", phone: "", service: SERVICES[4], message: "" };

const Section = ({ title, children, testId }) => (
  <Reveal>
    <div className="border-t border-navy/10 pt-10 mt-16" data-testid={testId}>
      <h2 className="font-serif text-2xl lg:text-3xl font-semibold text-navy mb-6">{title}</h2>
      <div className="space-y-5 text-sm lg:text-base text-navy/60 leading-relaxed max-w-3xl">
        {children}
      </div>
    </div>
  </Reveal>
);

const Lead = ({ children }) => (
  <strong className="text-navy font-semibold">{children}</strong>
);

const tiers = [
  {
    id: "tier-1",
    code: "Tier 1",
    title: "Heart Health Check",
    time: "All staff · ~20 minutes",
    points: [
      "Blood pressure measured properly and repeated where elevated",
      "Pulse and rhythm assessment for atrial fibrillation",
      "Height, weight, BMI and waist circumference",
      "Point-of-care cholesterol",
      "Personal and family history, smoking and alcohol",
      "QRISK3 cardiovascular risk score explained one to one",
      "Written summary for the individual, with a GP letter where findings need follow-up",
    ],
  },
  {
    id: "tier-2",
    code: "Tier 2",
    title: "Extended Cardiac Assessment",
    time: "~35 minutes",
    points: ["Everything in Tier 1", "12-lead ECG with specialist interpretation"],
  },
  {
    id: "tier-3",
    code: "Tier 3",
    title: "Specialist Cardiac Assessment",
    time: "~60 minutes",
    points: [
      "Everything in Tier 2",
      "Transthoracic echocardiography acquired and reported by an accredited practitioner, offered on clinical indication",
    ],
  },
];

export default function CorporateScreening() {
  const [form, setForm] = useState(initial);
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSending(true);
    try {
      await api.post("/contact", {
        name: form.name,
        organization: form.company,
        role: form.role,
        email: form.email,
        phone: form.phone,
        enquiry_type: `Corporate Screening — ${form.service}`,
        message: form.message,
      });
      setDone(true);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-ivory min-h-screen" data-testid="corporate-screening-page">
      <Nav />

      <section className="pt-32 pb-16 max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="font-mono text-[11px] tracking-[0.35em] uppercase text-gold mb-4">
            Cardiovascular Screening for Employers
          </p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-[#4169E1] leading-tight" data-testid="chs-title">
            Corporate Heart Health Screening
          </h1>
          <p className="mt-8 text-base text-navy/60 leading-relaxed max-w-2xl">
            Cardiovascular disease is the leading cause of death in working-age adults in
            the UK, and almost all of the risk is silent. High blood pressure, atrial
            fibrillation, raised cholesterol and structural heart disease cause no
            symptoms at all until they cause an event.
          </p>
          <p className="mt-5 text-base text-navy/60 leading-relaxed max-w-2xl">
            Screening is worthwhile when it finds those things and acts on them. Much of
            what is sold as corporate health screening instead offers tests with no
            evidence base in healthy adults, producing false alarms and avoidable anxiety.
          </p>
          <p className="mt-5 text-base text-navy font-medium leading-relaxed max-w-2xl border-l-2 border-gold pl-5">
            Our position is straightforward: every participant receives the assessment
            that has evidence behind it, and nobody receives an echocardiogram unless
            there is a clinical reason for one.
          </p>
        </motion.div>
      </section>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 pb-10">
        <Section title="Looking after your people" testId="chs-people">
          <p>
            The conditions we look for are the ones that cause strokes and heart attacks —
            and a stroke or heart attack in a member of staff in their forties or fifties
            is rarely a short absence. It is often months away from work, sometimes a
            permanent change in what that person can do, and occasionally a death in
            service.
          </p>
          <p>
            Those events are not inevitable. Detecting and treating atrial fibrillation
            prevents around two-thirds of the strokes it would otherwise cause. Most
            people carrying these risks feel entirely well and have no reason to see their
            GP.
          </p>
          <p>
            Screening is how you find them while they still feel well. For an employer,
            that is the practical meaning of a duty of care: not a policy document, but
            twenty minutes per person and a clear route to treatment for anyone who needs
            it.
          </p>
        </Section>

        <Section title="The scale of the problem" testId="chs-scale">
          <p>
            <Lead>Blood pressure.</Lead> Around one in three UK adults has high blood
            pressure, usually without knowing it. Approximately five million adults aged
            18 to 64 are living with it undiagnosed, and a further five million in England
            are diagnosed but not controlled to target.
          </p>
          <p>
            <Lead>Atrial fibrillation.</Lead> Over 1.4 million UK adults have AF and
            around 30% are undiagnosed. AF raises stroke risk around fivefold, one in five
            strokes is linked to AF, and AF-related strokes are more likely to be fatal or
            severely disabling. Up to 15% of strokes occur in people whose AF had never
            been diagnosed — roughly 30,000 strokes a year in the UK.
          </p>
          <p>
            <Lead>Structural heart disease.</Lead> More than 1.5 million people in the UK
            are living with heart valve disease. In a UK community study of symptom-free
            adults over 60 with no known heart disease, echocardiography found valve
            disease in 28% and clinically significant disease in 2.4%. Valve disease is
            diagnosed almost entirely by echocardiography, which is usually only requested
            once symptoms appear or a murmur is heard.
          </p>
          <p>
            <Lead>A note on age.</Lead> Blood pressure and cholesterol findings apply
            across the working-age population. Atrial fibrillation and valve disease are
            strongly age-related and uncommon below 60, so in a younger workforce the
            yield from rhythm and structural assessment is low. We say so rather than
            selling tiers that will find nothing.
          </p>
        </Section>

      </div>

      <section className="bg-navy py-24 mt-16" data-testid="chs-bhf-video-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal>
            <p className="font-mono text-[11px] tracking-[0.35em] uppercase text-gold mb-4">
              See It In Practice
            </p>
            <h2 className="font-serif text-2xl lg:text-4xl font-semibold text-ivory leading-tight mb-6">
              Workplace heart health in action.
            </h2>
            <p className="text-sm lg:text-base text-ivory/60 leading-relaxed max-w-2xl mb-12">
              Watch how the British Heart Foundation's Health at Work programme brings
              on-site heart health checks to employees — the same evidence-based approach
              to workplace cardiovascular screening that informs our own corporate service.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="relative max-w-4xl mx-auto">
              <div className="absolute -top-4 -right-4 w-full h-full border border-gold/50" />
              <div className="relative aspect-video bg-obsidian">
                <iframe
                  src="https://www.youtube-nocookie.com/embed/-ls94vXjfos"
                  title="British Heart Foundation — Health at Work, Health and Wellbeing Day"
                  className="absolute inset-0 w-full h-full"
                  loading="lazy"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  data-testid="chs-bhf-video-frame"
                />
              </div>
            </div>
            <p className="mt-8 text-center font-mono text-[10px] tracking-[0.25em] uppercase text-ivory/40" data-testid="chs-bhf-credit">
              Video courtesy of the{" "}
              <a
                href="https://www.bhf.org.uk/how-you-can-help/health-at-work"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gold hover:text-champagne transition-colors"
                data-testid="chs-bhf-credit-link"
              >
                British Heart Foundation · Health at Work
              </a>
            </p>
          </Reveal>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 pb-10">

        <Reveal>
          <div className="border-t border-navy/10 pt-10 mt-16" data-testid="chs-packages">
            <h2 className="font-serif text-2xl lg:text-3xl font-semibold text-navy mb-8">
              The packages
            </h2>
            <div className="grid md:grid-cols-3 gap-px bg-navy/10 border border-navy/10">
              {tiers.map((t) => (
                <div key={t.id} className="bg-white p-8 flex flex-col" data-testid={`chs-${t.id}`}>
                  <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-gold mb-2">
                    {t.code}
                  </p>
                  <h3 className="font-serif text-xl font-semibold text-navy">{t.title}</h3>
                  <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-navy/40 mt-1.5 mb-5">
                    {t.time}
                  </p>
                  <ul className="space-y-2.5 flex-1">
                    {t.points.map((p) => (
                      <li key={p} className="flex gap-3 text-xs text-navy/60 leading-relaxed">
                        <span className="text-gold shrink-0">✦</span>
                        {p}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            <p className="mt-6 text-sm text-navy/60 leading-relaxed max-w-3xl">
              <Lead>Add-ons.</Lead> 24-hour ambulatory blood pressure monitoring;
              ambulatory ECG monitoring; workforce atrial fibrillation screening events;
              heart health awareness sessions.
            </p>
          </div>
        </Reveal>

        <Section title="How it works on site" testId="chs-onsite">
          <p>
            We attend your premises with all equipment. We need a private room in which a
            participant can lie down and undress to the waist with dignity, and access to
            handwashing. Each participant receives their results face to face, in plain
            language, with time to ask questions.
          </p>
        </Section>

        <Section title="Medical review and doctor's report" testId="chs-medical">
          <p>
            Where an organisation requires it, screening findings can be reviewed by a
            doctor and issued as a medical report for each participant, alongside the
            physiologist's assessment and the GP summary letter. This can be arranged for
            all participants, or reserved for those with abnormal or borderline findings.
          </p>
        </Section>

        <Section title="Confidentiality" testId="chs-confidentiality">
          <p>
            Individual results belong to the individual and are never shared with the
            employer, HR or occupational health without that person's specific written
            consent. The employer receives an anonymised aggregate report only, with any
            figure derived from fewer than five participants suppressed so individuals
            cannot be identified within small teams. Participation is voluntary and
            non-participation is not reported.
          </p>
          <p>
            Every participant is told, before consenting, that findings from private
            screening may be disclosable when they next apply for life insurance, critical
            illness cover or a mortgage.
          </p>
        </Section>

        <Section title="Clinical safety net" testId="chs-safety">
          <p>
            Urgent findings follow a defined same-day escalation route agreed in writing
            before the first session. Findings needing medical action are summarised in
            writing for the participant's own GP. Participants with no GP follow a route
            agreed with the employer in advance. Every abnormal result has a named next
            step and a person attached to it.
          </p>
        </Section>

        <Section title="Why us" testId="chs-why">
          <p>
            The service is led by a cardiac physiologist accredited in echocardiography by
            the British Society of Echocardiography and the European Association of
            Cardio-Vascular Imaging, who assesses and examines other practitioners for
            those bodies, and who has spent years doing blood pressure and atrial
            fibrillation screening in community settings.
          </p>
          <p>
            The practical difference is what happens when something turns up. A borderline
            ECG or an unexpected murmur is interpreted on the spot by someone who reports
            these findings for a living, rather than triggering a referral that may have
            been avoidable.
          </p>
        </Section>

        <Section title="Costing" testId="chs-costing">
          <p>
            Each package is built around the size of your workforce, the age and risk
            profile of your staff, the number of sites and the tiers you wish to offer.
            Pricing is per participant or per session. There is no separate charge for
            reporting, aggregate analysis or GP summary letters.
          </p>
          <p>
            For more information and a costed proposal, contact us with your approximate
            headcount, location and timescale.
          </p>
        </Section>
      </div>

      <section id="enquire" className="bg-navy py-24 mt-16 scroll-mt-24" data-testid="chs-enquiry-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="font-mono text-[11px] tracking-[0.35em] uppercase text-gold mb-4">
                Discuss a Screening Programme
              </p>
              <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-semibold text-ivory leading-tight mb-6">
                Tell us about your workforce.
              </h2>
              <p className="text-sm text-ivory/60 leading-relaxed max-w-md">
                Share your approximate headcount, location and timescale, and we will
                respond within one working day with a costed proposal.
              </p>
              <p className="mt-6 text-xs text-ivory/40 leading-relaxed max-w-md border-l-2 border-gold/50 pl-4">
                Please do not include any patient-identifiable information in this form.
              </p>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            {done ? (
              <div className="border border-gold/50 bg-ivory p-10" data-testid="chs-form-success">
                <h3 className="font-serif text-2xl font-semibold text-navy mb-3">Enquiry received.</h3>
                <p className="text-sm text-navy/60 leading-relaxed">
                  Thank you — the CVI clinical team will review your requirements and
                  respond within one working day.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} className="grid sm:grid-cols-2 gap-5" data-testid="chs-enquiry-form">
                <div>
                  <label className={`${labelCls} !text-ivory/50`}>Name</label>
                  <input required value={form.name} onChange={set("name")} data-testid="chs-name-input" className={inputCls} placeholder="Jane Smith" />
                </div>
                <div>
                  <label className={`${labelCls} !text-ivory/50`}>Company</label>
                  <input required value={form.company} onChange={set("company")} data-testid="chs-company-input" className={inputCls} placeholder="Company Ltd" />
                </div>
                <div>
                  <label className={`${labelCls} !text-ivory/50`}>Role</label>
                  <input value={form.role} onChange={set("role")} data-testid="chs-role-input" className={inputCls} placeholder="HR Director / Office Manager" />
                </div>
                <div>
                  <label className={`${labelCls} !text-ivory/50`}>Email</label>
                  <input type="email" required value={form.email} onChange={set("email")} data-testid="chs-email-input" className={inputCls} placeholder="you@company.co.uk" />
                </div>
                <div>
                  <label className={`${labelCls} !text-ivory/50`}>Telephone</label>
                  <input type="tel" value={form.phone} onChange={set("phone")} data-testid="chs-phone-input" className={inputCls} placeholder="07…" />
                </div>
                <div>
                  <label className={`${labelCls} !text-ivory/50`}>Package of Interest</label>
                  <select value={form.service} onChange={set("service")} data-testid="chs-service-select" className={inputCls}>
                    {SERVICES.map((s) => (
                      <option key={s}>{s}</option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className={`${labelCls} !text-ivory/50`}>Message</label>
                  <textarea rows={5} required value={form.message} onChange={set("message")} data-testid="chs-message-input" className={inputCls} placeholder="Approximate headcount, location(s) and timescale…" />
                </div>

                {error && (
                  <p data-testid="chs-form-error" className="sm:col-span-2 text-sm text-crimson border border-crimson/30 bg-crimson/5 px-4 py-3">
                    {error}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={sending}
                  data-testid="chs-submit-button"
                  className="sm:col-span-2 w-full bg-gold text-navy font-mono text-xs tracking-[0.25em] uppercase py-4 hover:bg-champagne transition-colors disabled:opacity-50"
                >
                  {sending ? "Sending…" : "Discuss a Screening Programme"}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      <CtaFooter />
    </div>
  );
}
